"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Clock, Mail, Phone } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hubs = [{
  code: "BRN / 01 / LON",
  name: "London Central",
  region: "UK OPERATIONS",
  address: ["12 Baker Street", "Suite 10", "London, UK W1U 3BW"],
  phone: "+44 20 7946 0123",
  email: "london@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Arthur Pendelton",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_arthur_pendelton.png"
}, {
  code: "BRN / 02 / MAN",
  name: "Manchester Hub",
  region: "UK OPERATIONS",
  address: ["45 Deansgate", "Unit 5", "Manchester, UK M3 2PY"],
  phone: "+44 161 496 0456",
  email: "manchester@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Sarah Jenkins",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_sarah_jenkins.webp"
}, {
  code: "BRN / 03 / BIR",
  name: "Birmingham Depot",
  region: "MIDLANDS",
  address: ["88 New Street", "Floor 3", "Birmingham, UK B2 4BA"],
  phone: "+44 121 496 0789",
  email: "birmingham@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "David Thorne",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_david_thorne.webp"
}, {
  code: "BRN / 04 / GLA",
  name: "Glasgow Terminal",
  region: "SCOTLAND",
  address: ["120 Buchanan St", "Dock 1", "Glasgow, UK G1 2JA"],
  phone: "+44 141 496 0111",
  email: "glasgow@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Fiona MacLeod",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_fiona_macleod.png"
}, {
  code: "BRN / 05 / LEE",
  name: "Leeds Logistics",
  region: "NORTHERN OPS",
  address: ["33 Park Row", "Bay 2", "Leeds, UK LS1 5HD"],
  phone: "+44 113 496 0222",
  email: "leeds@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "James Wilson",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_james_wilson.png"
}, {
  code: "BRN / 06 / BRI",
  name: "Bristol Center",
  region: "SOUTH WEST",
  address: ["56 Broad Quay", "Dock 9", "Bristol, UK BS1 4DA"],
  phone: "+44 117 496 0333",
  email: "bristol@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Elena Rossi",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_elena_rossi.webp"
}, {
  code: "BRN / 07 / CAR",
  name: "Cardiff Hub",
  region: "WALES",
  address: ["22 St Mary St", "Suite 101", "Cardiff, UK CF10 1AD"],
  phone: "+44 29 2012 3456",
  email: "cardiff@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Gareth Evans",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_gareth_evans.webp"
}, {
  code: "BRN / 08 / BEL",
  name: "Belfast Depot",
  region: "NORTHERN IRELAND",
  address: ["15 Donegall Sq", "Warehouse A", "Belfast, UK BT1 5GS"],
  phone: "+44 28 9012 3456",
  email: "belfast@prime-logistics.co.uk",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Liam O'Connor",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669618/img/manager_liam_oconnor.png"
}];
export default function ContactInformation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: pillRef,
    isVisible: pillVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const firstRow = hubs.slice(0, 4);
  const secondRow = hubs.slice(4, 8);
  return <section data-section-id="823" id="contact-information" className="bg-background py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="inline-block size-1.5 rounded-lg bg-[var(--chart-1)] animate-pulse" />
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-foreground/70">
              Our regional network
            </span>
          </div>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-foreground">
            Eight locations. <span className="italic font-light">One commitment.</span>
          </h2>
          <p className="mt-6 font-sans text-base text-foreground/70 max-w-xl mx-auto">
            Expert logistics teams across the UK, providing seamless supply chain solutions from every major hub.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {firstRow.map((hub, i) => <HubCard key={hub.code} hub={hub} index={i} dataIndex={i} />)}
        </div>

        <div ref={pillRef} data-index={0} className={`relative my-10 flex justify-center transition-all duration-700 ease-out ${pillVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-foreground/10 hidden md:block" />
          <Link to="/ContactPage" className="relative inline-flex items-center gap-3 rounded-lg bg-[var(--chart-1)] px-6 py-3 font-mono text-[11px] md:text-xs tracking-[0.18em] uppercase text-primary-foreground hover:brightness-110 transition-all shadow-md">
            <span>Need a specific contact? Call our 24/7 desk</span>
            <span className="inline-block size-1.5 rounded-lg bg-primary-foreground animate-pulse" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {secondRow.map((hub, i) => <HubCard key={hub.code} hub={hub} index={i + 4} dataIndex={i} />)}
        </div>
      </div>
    </section>;
}
type Hub = {
  code: string;
  name: string;
  region: string;
  address: string[];
  phone: string;
  email: string;
  hours: string;
  status: string;
  manager: string;
  avatar: string;
};
function HubCard({
  hub,
  index,
  dataIndex
}: {
  hub: Hub;
  index: number;
  dataIndex: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const initials = hub.manager.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const telHref = `tel:${hub.phone.replace(/[^0-9+]/g, "")}`;
  const mailHref = `mailto:${hub.email}`;
  return <div ref={ref} data-index={dataIndex} style={{
    transitionDelay: `${index * 60}ms`
  }} className={`group relative bg-card border border-foreground/10 rounded-lg p-7 flex flex-col aspect-[4/5] transition-all duration-700 ease-out hover:-translate-y-0.5 hover:border-foreground/20 shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] tracking-[0.15em] text-foreground/60 group-hover:text-[var(--chart-1)] transition-colors">
          {hub.code}
        </span>
        <span className="inline-block size-1.5 rounded-lg bg-[var(--chart-1)] animate-pulse" />
      </div>

      <div className="mt-5">
        <h3 className="font-serif font-default text-[26px] leading-tight tracking-tight text-foreground">
          {hub.name}
        </h3>
        <p className="mt-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-foreground/55">
          {hub.region}
        </p>
      </div>

      <div className="my-5 h-px w-full bg-foreground/10" />

      <div className="space-y-4 flex-1">
        <div>
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-foreground/45 mb-1.5">
            Address
          </p>
          <div className="font-sans text-[13px] leading-relaxed text-foreground/80">
            {hub.address.map((line, i) => <div key={i} data-index={i}>
                {line}
              </div>)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Phone className="size-3.5 text-foreground/60 shrink-0" />
          <Link to={telHref} className="font-serif font-default text-[17px] text-foreground hover:text-[var(--chart-1)] transition-colors">
            {hub.phone}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <Mail className="size-3.5 text-foreground/60 shrink-0" />
          <Link to={mailHref} className="font-mono text-[12px] text-foreground/75 hover:text-foreground transition-colors break-all">
            {hub.email}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="size-3.5 text-foreground/60 shrink-0" />
          <span className="font-mono text-[12px] text-foreground/75">
            {hub.hours}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="inline-block size-1.5 rounded-lg bg-[var(--chart-2)]" />
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-foreground/70">
            {hub.status}
          </span>
        </div>
      </div>

      <div className="my-5 h-px w-full bg-foreground/10" />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <SafeHydrate><Avatar className="size-8 rounded-lg border border-foreground/10">
            <AvatarImage src={hub.avatar} alt={hub.manager} />
            <AvatarFallback className="rounded-lg bg-muted text-foreground text-[10px] font-mono">
              {initials}
            </AvatarFallback>
          </Avatar></SafeHydrate>
          <span className="font-sans text-[12px] text-foreground/80 truncate">
            {hub.manager}
          </span>
        </div>
        <Link to={telHref} className="font-mono text-[11px] tracking-[0.1em] text-foreground hover:text-[var(--chart-1)] transition-colors whitespace-nowrap">
          Direct dial →
        </Link>
      </div>
    </div>;
}